import { Box, Divider, Link, Typography } from "@mui/material";
import { usePageMeta } from "../hooks/usePageMeta";

export function TermsPage() {
    usePageMeta({
        title: 'Terms and Conditions',
        description: 'Terms and conditions for ColbrenSolutions LLC SMS and AI-powered phone services.',
    });

    return (
        <>
            <Box sx={{ mb: 4 }}>
                <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>Terms and Conditions</Typography>
                <Typography variant='body2' color='text.secondary'>ColbrenSolutions LLC — Last updated: May 15, 2026</Typography>
            </Box>

            <Box sx={{ maxWidth: 760 }}>

                <Section title="Overview">
                    These Terms and Conditions govern your use of SMS messaging and AI-powered phone services operated by
                    ColbrenSolutions LLC ("Colbren", "we", "us") on behalf of local businesses. By interacting with a
                    Colbren-powered service, you agree to these terms.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="SMS Messaging Terms">
                    <List items={[
                        'You consent to receive automated SMS notifications when you place an order, book an appointment, or request a service through a Colbren-powered phone system',
                        'Message types include: order confirmations, appointment confirmations, status updates, reminders, and service notifications',
                        'Message frequency depends on your interaction activity',
                        'Message and data rates may apply depending on your mobile carrier and plan',
                        'Supported carriers include but are not limited to: AT&T, Verizon, T-Mobile, Sprint, and other major US carriers',
                    ]} />
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Opt-Out and Help">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>To stop receiving messages:</strong> Reply STOP to any message. You will receive one
                            confirmation message and no further messages will be sent.
                        </Typography>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>To resume messages:</strong> Reply START at any time
                        </Typography>
                        <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                            <strong>For assistance:</strong> Reply HELP or contact us at{' '}
                            <Link href='mailto:info@colbrensolutions.com'>info@colbrensolutions.com</Link>
                        </Typography>
                    </Box>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="No Warranty">
                    SMS delivery is subject to carrier availability and network conditions. Colbren does not guarantee delivery
                    of messages and is not responsible for delays or failures caused by third-party carriers or services.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Limitation of Liability">
                    ColbrenSolutions LLC is not liable for any damages arising from your use of or inability to use our SMS or
                    phone services, including but not limited to missed order confirmations or appointment notifications.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Changes to These Terms">
                    We may update these Terms at any time. The current version will always be available at{' '}
                    <Link href='https://colbren.com/terms' target='_blank' rel='noopener noreferrer'>
                        https://colbren.com/terms
                    </Link>
                    . Continued use of our services constitutes acceptance of the updated terms.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Governing Law">
                    These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.
                </Section>

                <Divider sx={{ my: 4 }} />

                <Section title="Contact">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant='body1'><strong>ColbrenSolutions LLC</strong></Typography>
                        <Typography variant='body1'>
                            Email: <Link href='mailto:info@colbrensolutions.com'>info@colbrensolutions.com</Link>
                        </Typography>
                        <Typography variant='body1'>
                            Web: <Link href='https://colbren.com/terms' target='_blank' rel='noopener noreferrer'>
                                https://colbren.com/terms
                            </Link>
                        </Typography>
                    </Box>
                </Section>

            </Box>
        </>
    );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 0 }}>
            <Typography variant='h6' sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
            {typeof children === 'string'
                ? <Typography variant='body1' sx={{ lineHeight: 1.8 }}>{children}</Typography>
                : children
            }
        </Box>
    );
}

function List({ items }: { items: string[] }) {
    return (
        <Box component='ul' sx={{ mt: 1, mb: 0, pl: 3 }}>
            {items.map(item => (
                <Box component='li' key={item} sx={{ mb: 0.5 }}>
                    <Typography variant='body1' sx={{ lineHeight: 1.8 }}>{item}</Typography>
                </Box>
            ))}
        </Box>
    );
}
